import { FocusKeyManager } from '@angular/cdk/a11y';
import { ENTER, UP_ARROW } from '@angular/cdk/keycodes';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit, ChangeDetectorRef, Component, ContentChild, Directive, EventEmitter, Input,
  OnChanges, OnInit, Output, QueryList, SimpleChanges, TemplateRef, ViewChild, ViewChildren
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QueryRef } from 'apollo-angular';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';

import {
  SearchTags, SearchTagsGetAllIdsGQL, SearchTagsGQL, SearchTagsQuery, SearchTagsQueryVariables,
  TagStatus, TagType, UserRole
} from 'generated/graphql';

import { AccountRoleService } from 'auth/account-role.service';
import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { getPaginationAfterCursor, isNilOrEmpty, uuid } from 'helpers';
import { ListItemComponent } from 'shared/components/list-item/list-item.component';
import { CustomInfiniteScrollNextPageManager } from 'shared/CustomInfiniteScrollNextPageManager';
import { AutofocusDirective } from 'shared/directives/focus.directive';

import { ResponseItemTmplDirective } from './response-item-templ.directive';
import { SET_TAG_SEARCH_TEXT } from './state/response-search.actions';
import { selectResponseSearchText } from './state/response-search.selectors';
import { ResponseSearchComponentState } from './state/response-search.state';

/** Used as template for no results */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[responseNoResultsTmpl]'
})
export class ResponseNoResultsTmplDirective {
}

/**
 *  Searches
 */
@Component({
  selector: 'app-response-search',
  templateUrl: `./response-search.component.html`,
  styleUrls: [`./response-search.component.scss`]
})
export class ResponseSearchComponent extends WithDisposableSubject(WithChangesSubject(class { }))
  implements OnInit, OnChanges, AfterViewInit {

  @Input() allowSelectAll = false;

  /**
   * Allow user to select all, up to a maximum number.
   * Set to 0 to disable.
   */
  @Input() allowSelectMax = 0;


  @Input() hideUniversal: boolean;

  /**
   * Disable autofocus
   */
  @Input() autoFocusDisabled = false;

  /**
   * Unique id for the component
   * Used to subscribe to store for filter text input
   */
  @Input() componentId: string;

  /**
   * Archive status for search
   * default will only return active tags
   */
  @Input() archiveStatus: TagStatus;

  @Input() hideReadonly: boolean;
  /**
   * Limit to EntityType or EntityConnectionType
   * also required if targetTypeId is provided
   */
  @Input() targetType: 'EntityType' | 'EntityConnectionType' | 'CalendarEventType';

  /**
   * Limit responses to only tags for the given
   * EntityType id or EntityConnectionType id
   */
  @Input() targetTypeId: string;

  /**
   * Optional: Limit to specific tag category ids
   */
  @Input() tagCategoryIds: string[] = [];

  /** Optional, will filter to only tags in a campaign */
  @Input() limitToCampaignId: string;

  /** Campaign to check availability */
  @Input() checkAvailableCampaignId: string;

  /** Filter to only these tag types */
  @Input() tagTypes: Array<TagType>;

  /**
   * If true tags will be ordered by the name of the
   * EntityType or EntityConnectionType
   */
  @Input() orderByTargetName: boolean;

  /** If true, the cdk scroller that holds the responses (tags)
   * will get a slighter greater height per result, since the field name is also shown.
   * (this fixes a weird scrolling issue that arises with result sets of less than 4)
   */
  @Input() isAdvancedResponseSelector= false;

  @Input() selectAllLoading: boolean;

  @Input() selectedResponses: any = null;

  @Output() selectAllLoadingChange = new EventEmitter<boolean>();

  @Output() itemSelected = new EventEmitter<SearchTags.Node>();

  @Output() allSelected = new EventEmitter<string[]>();

  @Output() hasResponses = new EventEmitter();

  // Read in our structural directives as TemplateRefs
  @ContentChild(ResponseItemTmplDirective, { read: TemplateRef }) responseItemTemplate;

  // Read in our structural directives as TemplateRefs
  @ContentChild(ResponseNoResultsTmplDirective, { read: TemplateRef }) noResultsTemplate;

  @ViewChild('scrollContainer', { static: true })
  private _scrollContainer: CdkVirtualScrollViewport;

  @ViewChildren(ListItemComponent) items: QueryList<ListItemComponent>;

  @ViewChild('searchElement') searchElement: AutofocusDirective;

  filterInput = new UntypedFormControl();

  // True when data is loading
  loading = true;

  // Initially true, since data is loading
  initLoading = true;

  // Hide search input when very few results exist
  hideSearchInput = false;

  /** Items fetched per page from graphql */
  readonly itemsPerPage = 50;

  resultsQuery: QueryRef<SearchTagsQuery, SearchTagsQueryVariables>;

  totalCount: number;

  // The retrieved results
  results: SearchTags.Node[] = [];

  private keyManager: FocusKeyManager<ListItemComponent>;

  private nextPageManager: CustomInfiniteScrollNextPageManager<any>;

  searchText$: Observable<string>;

  listItemSize: number;

  maxListItemsInScroller: number;

  selectAllIcon: 'square' | 'square-check' = 'square';

  /**
   * Get the order by parameter based upn
   * the current targetType and orderByTargetName
   * variable.
   */
  get orderBy() {
    if (!this.orderByTargetName) {
      return null;
    }

    if (this.targetType === 'EntityConnectionType') {
      return 'connection_name';
    }

    if (this.targetType === 'EntityType') {
      return 'entity_type_name';
    }
  }
  /**
   *
   */
  constructor(
    private _cd: ChangeDetectorRef,
    private _searchTagsGQL: SearchTagsGQL,
    private _actions: Actions,
    private _store: Store<ResponseSearchComponentState>,
    private _accountRoleService: AccountRoleService,
    private _searchTagsGetAllIdsGQL: SearchTagsGetAllIdsGQL
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  // **Life Cycle Hooks** //
  ngOnInit() {

    this.listItemSize = (this.isAdvancedResponseSelector) ? 63 : 48;
    this.maxListItemsInScroller = (this.isAdvancedResponseSelector) ? 4 : 5;

    if (this.componentId == null) {
      this.componentId = uuid();
    }

    this._resetOnInputPropChanges();

    this.searchText$ = this._store.select(
      selectResponseSearchText({ componentId: this.componentId })
    ).pipe(
      takeUntil(
        this.disposer
      )
    );

    this.searchText$.subscribe((text) => {
      this.filterInput.setValue(text);
    });

    this.filterInput.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe((value) => {
      this._store.dispatch(SET_TAG_SEARCH_TEXT({ componentId: this.componentId, text: value }));
    });

    // Set the search
    this.resultsQuery = this._searchTagsGQL.watch(
      this.getFetchMoreVariables(0)
    );
    // Sets the results visible in the page.
    this.resultsQuery.valueChanges
      .pipe(
        takeUntil(this.disposer),
        filter(r => !r.loading),
        filter(r => !!r.data)
      )
      .subscribe(r => {
        this.loading = false;
        this.totalCount = r.data.searchTags.totalCount;
        this.results = r.data.searchTags.edges.map(e => e.node).filter(
          // filter readonly
          (tag) => {
            if (!this.hideReadonly) {
              return true;
            }
            const isReadonly = tag?.category?.readonly;

            if (isReadonly && this._accountRoleService.userHasMinRole(UserRole.Administrator)) {
              return true;
            }

            return !isReadonly;
          }
        ).filter((tag) => {
          if (!this.hideUniversal) { return true; }
          return tag?.isUniversal === false;
        });

        // Hide the search input when fewer than 1 total tags
        if (isNilOrEmpty(this.filterInput.value)) {
          if (this.hideReadonly || this.hideUniversal) { //TODO: This is ugly
            this.hasResponses.emit(this.results.length >= 1);
            this.hideSearchInput = this.results.length === 0;
          } else {
            this.hasResponses.emit(r.data.searchTags.totalCount >= 1);
            this.hideSearchInput = r.data.searchTags.totalCount === 0;
          }
        } else {
          this.hideSearchInput = false;
        }
        this.initLoading = false;

        this._cd.detectChanges();
        this._cd.markForCheck();
      });
  }

  _resetOnInputPropChanges() {
    this.watchForChangeKeys(
      'tagCategoryIds',
      'limitToCampaignId',
      'hideUniversal',
      'checkAvailableCampaignId',
      'targetType',
      'targetTypeId',
      'hideReadonly',
      'archiveStatus',
      'tagTypes'
    ).subscribe(t => {
      this.filterInput.setValue('');
      this._reset();
    });
  }

  ngAfterViewInit() {
    this.nextPageManager = new CustomInfiniteScrollNextPageManager<any>(
      this.getScrollContainer(),
      this.itemsPerPage
    );
    this.keyManager = new FocusKeyManager(this.items);
    this.nextPageManager.nextPageNeeded.subscribe((page) => {
      if (page > 0) {
        this.executeFetchMore(this.getFetchMoreVariables(page));
      }
    });
    this._watchFilter();
  }

  private _watchFilter() {
    this.searchText$
      .pipe(
        takeUntil(this.disposer),
        tap(() => {
          this.loading = true;
          this._cd.markForCheck();
        }),
        distinctUntilChanged(),
        debounceTime(100)
      )
      .subscribe(() => {
        this.keyManager.updateActiveItem(null);
        this._cd.detectChanges();
        this._cd.markForCheck();
        this.resultsQuery.resetLastResults();
        this.resultsQuery.setVariables(this.getFetchMoreVariables(0)).then(() => {
          this.nextPageManager.reset();
        });
      });
  }

  private async _reset() {
    this.keyManager.updateActiveItem(null);
    this._cd.detectChanges();
    this._cd.markForCheck();
    this.resultsQuery.resetLastResults();
    await this.resultsQuery.setVariables(this.getFetchMoreVariables(0));
    this.nextPageManager.reset();
  }

  getScrollContainer(): CdkVirtualScrollViewport {
    return this._scrollContainer;
  }

  getFetchMoreVariables(page: number) {
    return {
      keyword: this.filterInput.value || null,
      tagCategoryIds: this.tagCategoryIds,
      targetType: this.targetType,
      targetTypeId: this.targetTypeId,
      tagTypes: this.tagTypes,
      campaignId: this.limitToCampaignId,
      orderBy: this.orderBy,
      status: this.archiveStatus,
      includeNotInCampaign: this.limitToCampaignId ? false : true,
      isInCampaignId: this.checkAvailableCampaignId || this.limitToCampaignId,
      first: this.itemsPerPage,
      after: getPaginationAfterCursor(this.itemsPerPage, page)
    };
  }

  executeFetchMore(vars: any) {
    return this.resultsQuery.fetchMore({
      variables: vars
    });
  }

  /**
   * Keyboard actions for the results list
   *
   * @param event keyboard event
   */
  onResultsListKeydown(event) {
    if (!this.keyManager) {
      return;
    }
    if (event.keyCode === ENTER) {
      const item = this.keyManager.activeItem?.item;
      if (item) {
        this.itemSelected.emit(item);
      }
    } else {
      // Focus search box if up arrow and activeItemIdex is 0
      if (event.keyCode === UP_ARROW) {
        if (this.keyManager.activeItemIndex === 0) {
          this.searchElement?.focusElement();
        }
      }
      this.keyManager.onKeydown(event);
    }
  }

  onSearchArrowDown() {
    if (!this.keyManager) {
      return;
    }
    this.keyManager.setFirstItemActive();
  }

  /**
   * Triggered when item is selected
   *
   * @param tag Selected item
   */
  itemClicked(tag: SearchTags.Node) {
    if (!this.keyManager) {
      return;
    }
    const activeItem = this.items.find(i => i.item?.id === tag.id);
    this.keyManager.setActiveItem(activeItem);
    this.itemSelected.emit(tag);
    this._cd.markForCheck();
  }

  selectAllClicked() {
    if (this.selectAllLoading) {
      return;
    }

    this.selectAllLoadingChange.emit(true);

    const variables = this.getFetchMoreVariables(0);
    this._searchTagsGetAllIdsGQL.fetch(variables, {
      fetchPolicy: 'no-cache'
    }).subscribe(r => {
      this.allSelected.emit(r.data.searchTags.matchingIds);
    });
  }

  selectMaxClicked() {
    if (this.selectAllLoading) {
      return;
    }

    this.selectAllLoadingChange.emit(true);

    const variables = this.getFetchMoreVariables(0);
    variables.first = this.allowSelectMax;
    this._searchTagsGetAllIdsGQL.fetch(variables, {
      fetchPolicy: 'no-cache'
    }).subscribe(r => {
      const firstmax = r.data.searchTags.matchingIds.map(i => i)
        .slice(0, this.allowSelectMax);

      this.allSelected.emit(firstmax);
    });
  }

  getSelectMaxCount() {
    return (this.allowSelectMax >= this.totalCount) ? 'All ' + this.totalCount : 'First ' + this.allowSelectMax;
  }

  getSelectAllIcon() {
    // if `selectedResponses` (array) is provided, use it to determine icon
    if ((this.allowSelectMax || this.allowSelectAll) && Array.isArray(this.selectedResponses)) {
      const selectedCount  = this.selectedResponses.length;

      if(this.allowSelectMax && (selectedCount === this.allowSelectMax || selectedCount >= this.totalCount)) {
        this.selectAllIcon = 'square-check';
      } else if (this.allowSelectAll && selectedCount === this.totalCount) {
        this.selectAllIcon = 'square-check';
      } else {
        this.selectAllIcon = 'square';
      }
    }

    const prefix = (this.selectAllIcon === 'square') ? 'far' : 'fas';
    return prefix + ' fa-' + this.selectAllIcon;
  }

}
